// src/hooks/usePropertyBidding.js
// Hook para gestión de ofertas y notificaciones

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBidIntent } from '../services/supabaseService';
import { getPropertyStatus } from '../utils/propertyHelpers';
import { validateLastChanceBid, canUserBidInLastChance } from '../config/lastChanceConfig';
import { useBiddingTracking } from './useBiddingTracking';

/**
 * Hook personalizado para gestionar el proceso completo de ofertas
 * Maneja validación, confirmación, condiciones y tracking automático de formulario
 * @param {Object} property - Datos de la propiedad
 * @param {Object} invite - Datos de la invitación del comprador
 * @param {Array} propertyBids - Ofertas actuales de la propiedad
 * @param {Array} propertyInvites - Lista de invitados
 * @param {Function} refreshBids - Callback para recargar ofertas después de pujar
 * @param {Function} showNotification - Callback para mostrar notificaciones al usuario
 * @param {Function} [trackEvent=null] - Callback opcional para tracking de engagement
 * @returns {Object} Estado y handlers del formulario
 * @returns {string} returns.bidAmount - Monto actual de la oferta
 * @returns {Function} returns.setBidAmount - Setter con tracking integrado
 * @returns {Array} returns.selectedConditions - Condiciones seleccionadas
 * @returns {Function} returns.setSelectedConditions - Setter con tracking
 * @returns {string} returns.conditionsText - Texto de condiciones personalizadas
 * @returns {Function} returns.setConditionsText - Setter con tracking
 * @returns {boolean} returns.showConfirmModal - Si modal de confirmación está abierto
 * @returns {Function} returns.handleBidSubmit - Handler principal para enviar oferta
 * @returns {string} returns.validationError - Mensaje de error de validación
 * @returns {Object} returns.biddingTracking - Objeto con funciones de tracking
 */
export const usePropertyBidding = (
  property,
  invite,
  propertyBids,
  propertyInvites,
  refreshBids,
  showNotification,
  trackEvent = null
) => {
  const { t } = useTranslation();

  // ============================================
  // ESTADOS DE BIDDING
  // ============================================
  const [bidAmount, setBidAmount] = useState('');
  const [confirmAmount, setConfirmAmount] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Estados de condiciones
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [conditionsText, setConditionsText] = useState('');

  // ============================================
  // TRACKING DELEGADO
  // ============================================
  const biddingTracking = useBiddingTracking(
    trackEvent,
    bidAmount,
    selectedConditions,
    conditionsText
  );

  // ============================================
  // HANDLERS CON TRACKING INTEGRADO
  // ============================================

  /**
   * Handler: Set bidAmount con tracking automático
   */
  const handleSetBidAmount = (value) => {
    // Iniciar timer en primer input
    if (value && !bidAmount) {
      biddingTracking.initFormTimer();
    }
    setBidAmount(value);
  };

  /**
   * Handler: Set selectedConditions con tracking
   */
  const handleSetSelectedConditions = (newConditions) => {
    setSelectedConditions(newConditions);
    biddingTracking.trackConditionsChange(newConditions);
  };

  /**
   * Handler: Set conditionsText con tracking
   */
  const handleSetConditionsText = (text) => {
    setConditionsText(text);
    if (text.length > 10) {
      biddingTracking.trackCustomConditionsWritten(text);
    }
  };

  /**
   * Iniciar el proceso de oferta (validación inicial)
   * ✅ MODIFICADO: Incluye lógica especial para LAST_CHANCE + ya NO verifica PIN
   */
  const handleBidSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(bidAmount);
    const highestBid = propertyBids[0];
    const currentPrice = highestBid ? highestBid.amount : property.startPrice;
    const propertyStatus = getPropertyStatus(property);

    // ✅ VALIDACIÓN PARA OFERTA CIEGA (BLIND_WAITING / BLIND_ACTIVE / UPCOMING con acceso anticipado)
    const isBlindBidSubmit = propertyStatus === 'BLIND_WAITING' || propertyStatus === 'BLIND_ACTIVE'
      || (propertyStatus === 'UPCOMING' && !!invite?.canBlindBid);
    if (isBlindBidSubmit) {
      // ✅ SIMPLIFICADO: Todos los invitados con token pueden ofertar
      // No hay restricción de canBlindBid - la invitación es suficiente
      // No hay validación de minAbsolute - total libertad en ofertas ciegas

      // Validar que el monto sea válido y positivo
      if (!amount || isNaN(amount) || amount <= 0) {
        setValidationError(t('validation.invalidAmount'));
        return;
      }

      // En fase ciega, el semáforo es puramente informativo
      // No bloqueamos ofertas bajas - el admin decidirá en la selección final

      setValidationError('');
      biddingTracking.trackSubmitSuccess(amount);
      openConfirmModal();
      return;
    }

    // ✅ VALIDACIÓN ESPECIAL PARA LAST_CHANCE
    if (propertyStatus === 'LAST_CHANCE') {
      // Verificar si el usuario puede pujar en última oportunidad
      if (!canUserBidInLastChance(invite.email, propertyBids, property.id)) {
        setValidationError(t('validation.lastChanceUsed'));

        biddingTracking.trackSubmitFailed(
          { isValid: false, errorKey: 'validation.lastChanceUsed' },
          amount,
          currentPrice,
          property
        );
        return;
      }

      // Validar el monto con reglas de última oportunidad
      const userBids = propertyBids.filter(b => b.buyerEmail === invite.email);
      const userLastBid = userBids.length > 0 ? userBids[0].amount : 0;
      const maxBid = highestBid ? highestBid.amount : property.startPrice;

      const validation = validateLastChanceBid(amount, userLastBid, maxBid, property.minAbsolute);

      if (!validation.valid) {
        setValidationError(t(validation.errorKey, validation.errorParams));
        biddingTracking.trackSubmitFailed(validation, amount, currentPrice, property);
        return;
      }
    } 
    // ✅ NUEVO: Validación básica de frontend para periodo normal
    else {
      // Validar que el monto no esté vacío o sea inválido
      if (!amount || isNaN(amount) || amount <= 0) {
        setValidationError(t('validation.invalidAmount'));
        biddingTracking.trackSubmitFailed(
          { isValid: false, errorKey: 'validation.invalidAmount' },
          amount,
          currentPrice,
          property
        );
        return;
      }

      // Calcular la oferta ciega previa del usuario (si existe)
      const userBlindOffer = propertyBids.find(
        b => b.buyerEmail === invite.email &&
        (b.sentInStatus === 'BLIND_WAITING' || b.sentInStatus === 'BLIND_ACTIVE')
      );

      // ✅ NUEVO: En fase OPEN, validar que no se puje por debajo de la oferta ciega
      if (propertyStatus === 'OPEN' && userBlindOffer && userBlindOffer.amount) {
        if (amount < userBlindOffer.amount) {
          setValidationError(t('validation.belowBlindOffer', { amount: userBlindOffer.amount.toLocaleString() }));
          biddingTracking.trackSubmitFailed(
            { isValid: false, errorKey: 'validation.belowBlindOffer' },
            amount,
            currentPrice,
            property
          );
          return;
        }
      }

      // Validar mínimo absoluto
      if (amount < property.minAbsolute) {
        setValidationError(t('validation.tooLow'));
        biddingTracking.trackSubmitFailed(
          { isValid: false, errorKey: 'validation.tooLow' },
          amount,
          currentPrice,
          property
        );
        return;
      }

      // Validar incremento mínimo (solo si ya hay ofertas previas)
      if (highestBid) {
        const minRequired = highestBid.amount + property.minIncrement;
        if (amount < minRequired) {
          setValidationError(t('validation.belowMinIncrement'));
          biddingTracking.trackSubmitFailed(
            { isValid: false, errorKey: 'validation.belowMinIncrement' },
            amount,
            currentPrice,
            property
          );
          return;
        }
      }

      // Validar máximo permitido (incremento máximo por puja)
      if (property.maxBidPercentage) {
        const baseForMax = highestBid ? highestBid.amount : property.startPrice;
        const maxAllowed = baseForMax * (1 + property.maxBidPercentage / 100);
        if (amount > maxAllowed) {
          setValidationError(t('validation.tooHigh'));
          biddingTracking.trackSubmitFailed(
            { isValid: false, errorKey: 'validation.tooHigh' },
            amount,
            currentPrice,
            property
          );
          return;
        }
      }
    }

    setValidationError('');

    // ✅ TRACKING: Envío exitoso (pasa validación)
    biddingTracking.trackSubmitSuccess(amount);

    // ✅ MODIFICADO: Abrir directamente el modal de confirmación (sin verificar PIN)
    openConfirmModal();
  };

  /**
   * Abrir modal de confirmación
   * ✅ MODIFICADO: Ya no requiere verificación de PIN previa
   */
  const openConfirmModal = () => {
    setShowConfirmModal(true);
    setConfirmAmount('');

    // ✅ TRACKING: Modal abierto
    biddingTracking.trackConfirmModalOpened(parseFloat(bidAmount), false);
  };

  /**
   * Cerrar modal de confirmación (abandono)
   */
  const closeConfirmModal = () => {
    if (showConfirmModal) {
      biddingTracking.trackConfirmModalAbandoned(parseFloat(bidAmount));
    }
    setShowConfirmModal(false);
  };

  /**
   * Confirmar y crear bid_intent + enviar email de verificación
   * ✅ NUEVO: En lugar de crear la oferta directamente, crea un intent y envía email
   */
  const handleConfirmBid = async () => {
    const amount = parseFloat(bidAmount);

    // Validar que los montos coincidan
    if (confirmAmount !== bidAmount) {
      // ✅ TRACKING: Error en confirmación
      biddingTracking.trackConfirmAmountMismatch(
        amount,
        parseFloat(confirmAmount) || null
      );
      
      showNotification('error', 'Error', t('validation.amountMismatch'));
      return;
    }

    try {
      setShowConfirmModal(false);

      // Detectar tipo de oferta
      const propertyStatus = getPropertyStatus(property);
      const isLastChanceBid = propertyStatus === 'LAST_CHANCE';
      const isEarlyBlindBid = propertyStatus === 'UPCOMING' && !!invite?.canBlindBid;
      const sentInStatus = isEarlyBlindBid ? 'UPCOMING_BLIND' : propertyStatus;

      // Crear bid_intent
      const intent = await createBidIntent({
        propertyId: property.id,
        buyerEmail: invite.email,
        buyerName: invite.name,
        amount: amount,
        selectedConditions: selectedConditions,
        conditionsText: conditionsText,
        sentInStatus: sentInStatus
      });

      // Enviar email de verificación
      const emailPayload = {
        token: intent.token,
        buyerEmail: invite.email,
        buyerName: invite.name,
        amount: amount,
        propertyTitle: property.title,
        baseUrl: window.location.origin,
        inviteToken: invite.token
      };

      const response = await fetch('/api/send-bid-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });

      if (!response.ok) {
        throw new Error('Error enviando email de verificación');
      }

      // ✅ TRACKING: Email de verificación enviado
      const isFirstBid = propertyBids.filter(b => b.buyerEmail === invite.email).length === 0;
      biddingTracking.trackBidVerificationEmailSent(amount, isFirstBid);

      // Resetear formulario y tracking
      setBidAmount('');
      setConfirmAmount('');
      setSelectedConditions([]);
      setConditionsText('');
      biddingTracking.resetTracking();

      // Mensaje de éxito unificado para ofertas normales y ciegas
      const successMessage = isLastChanceBid
        ? t('notifications.lastChanceEmailSent', { email: invite.email, amount: amount.toLocaleString() })
        : t('notifications.bidEmailSent', { email: invite.email, amount: amount.toLocaleString() });

      showNotification(
        'info',
        t('notifications.checkEmail'),
        successMessage,
        { persistent: true }
      );
      
    } catch (error) {
      console.error('Error creando bid intent:', error);
      
      // ✅ TRACKING: Error
      biddingTracking.trackBidCreationError(amount, error);
      
      showNotification('error', 'Error', t('notifications.bidError'));
    }
  };

  // ============================================
  // RETURN: API pública del hook
  // ============================================

  return {
    // Estados de bidding
    bidAmount,
    setBidAmount: handleSetBidAmount,
    confirmAmount,
    setConfirmAmount,
    validationError,
    setValidationError,
    showConfirmModal,
    setShowConfirmModal: closeConfirmModal,
    openConfirmModal,
    
    // Estados de condiciones
    selectedConditions,
    setSelectedConditions: handleSetSelectedConditions,
    conditionsText,
    setConditionsText: handleSetConditionsText,
    
    // Funciones principales
    handleBidSubmit,
    handleConfirmBid,
    // Alias para ofertas ciegas (usa el mismo flujo)
    handleBlindBidSubmit: handleBidSubmit,
  };
};

export default usePropertyBidding;